import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

const CONTACT_EMAIL = 'care@geofleet.ai';

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <>
      {items.map((text, i) => (
        <p key={i} className={i < items.length - 1 ? 'mb-3' : undefined}>
          {text}
        </p>
      ))}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="list-disc pl-6 space-y-1 mt-2">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function NumberedList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ol className="list-decimal pl-6 space-y-1 mt-2">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  );
}

function Subsection({
  title,
  intro,
  paragraphs,
  items,
  numbered,
  closing,
  note,
}: {
  title: string;
  intro?: string;
  paragraphs?: string[];
  items?: string[];
  numbered?: string[];
  closing?: string[];
  note?: string;
}) {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {intro && <p className="mb-2">{intro}</p>}
      {paragraphs && paragraphs.length > 0 && <Paragraphs items={paragraphs} />}
      {items && <BulletList items={items} />}
      {numbered && <NumberedList items={numbered} />}
      {closing && closing.length > 0 && (
        <div className="mt-3">
          <Paragraphs items={closing} />
        </div>
      )}
      {note && <p className="mt-3">{note}</p>}
    </div>
  );
}

export default function PrivacyPage() {
  const { t } = useTranslation();

  const intro = asStringArray(t('privacy.intro', { returnObjects: true }));

  const accountItems = asStringArray(t('privacy.sections.informationWeCollect.account.items', { returnObjects: true }));
  const deliveryItems = asStringArray(t('privacy.sections.informationWeCollect.delivery.items', { returnObjects: true }));
  const deviceItems = asStringArray(t('privacy.sections.informationWeCollect.device.items', { returnObjects: true }));
  const paymentItems = asStringArray(t('privacy.sections.informationWeCollect.payment.items', { returnObjects: true }));
  const communicationsItems = asStringArray(
    t('privacy.sections.informationWeCollect.communications.items', { returnObjects: true })
  );

  const googleIntro = asStringArray(t('privacy.sections.googleWorkspace.intro', { returnObjects: true }));
  const driveParagraphs = asStringArray(t('privacy.sections.googleWorkspace.drive.paragraphs', { returnObjects: true }));
  const driveItems = asStringArray(t('privacy.sections.googleWorkspace.drive.items', { returnObjects: true }));
  const driveClosing = asStringArray(t('privacy.sections.googleWorkspace.drive.closing', { returnObjects: true }));
  const sheetsParagraphs = asStringArray(t('privacy.sections.googleWorkspace.sheets.paragraphs', { returnObjects: true }));
  const sheetsNumbered = asStringArray(t('privacy.sections.googleWorkspace.sheets.numbered', { returnObjects: true }));
  const sheetsClosing = asStringArray(t('privacy.sections.googleWorkspace.sheets.closing', { returnObjects: true }));

  const googleProcessing = asStringArray(t('privacy.sections.googleProcessing.paragraphs', { returnObjects: true }));
  const googleLimitedUse = asStringArray(t('privacy.sections.googleLimitedUse.paragraphs', { returnObjects: true }));
  const howWeUseItems = asStringArray(t('privacy.sections.howWeUse.items', { returnObjects: true }));
  const aiMl = asStringArray(t('privacy.sections.aiMl.paragraphs', { returnObjects: true }));

  const serviceProviders = asStringArray(t('privacy.sections.sharing.serviceProviders.paragraphs', { returnObjects: true }));
  const legalItems = asStringArray(t('privacy.sections.sharing.legal.items', { returnObjects: true }));
  const business = asStringArray(t('privacy.sections.sharing.business.paragraphs', { returnObjects: true }));
  const authorization = asStringArray(t('privacy.sections.sharing.authorization.paragraphs', { returnObjects: true }));

  const googleAuth = asStringArray(t('privacy.sections.googleAuth.paragraphs', { returnObjects: true }));
  const retentionItems = asStringArray(t('privacy.sections.retention.items', { returnObjects: true }));
  const retentionParagraphs = asStringArray(t('privacy.sections.retention.paragraphs', { returnObjects: true }));
  const securityParagraphs = asStringArray(t('privacy.sections.security.paragraphs', { returnObjects: true }));
  const securityItems = asStringArray(t('privacy.sections.security.items', { returnObjects: true }));
  const rightsItems = asStringArray(t('privacy.sections.rights.items', { returnObjects: true }));
  const cookiesItems = asStringArray(t('privacy.sections.cookies.items', { returnObjects: true }));
  const international = asStringArray(t('privacy.sections.international.paragraphs', { returnObjects: true }));
  const children = asStringArray(t('privacy.sections.children.paragraphs', { returnObjects: true }));
  const thirdParty = asStringArray(t('privacy.sections.thirdParty.paragraphs', { returnObjects: true }));
  const changes = asStringArray(t('privacy.sections.changes.paragraphs', { returnObjects: true }));

  return (
    <div className="pt-24 pb-20">
      <SEO title={t('privacy.title')} description={t('privacy.seoDescription')} />
      <article className="max-w-3xl mx-auto px-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('privacy.title')}</h1>
        <p className="text-gray-500 text-sm mb-12">
          {t('privacy.lastUpdated')} {t('privacy.lastUpdatedDate')}
        </p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-600">
          <section>
            <Paragraphs items={intro} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t('privacy.sections.informationWeCollect.title')}
            </h2>
            <p className="mb-3">{t('privacy.sections.informationWeCollect.intro')}</p>
            <Subsection
              title={t('privacy.sections.informationWeCollect.account.title')}
              intro={t('privacy.sections.informationWeCollect.account.intro')}
              items={accountItems}
            />
            <Subsection
              title={t('privacy.sections.informationWeCollect.delivery.title')}
              intro={t('privacy.sections.informationWeCollect.delivery.intro')}
              items={deliveryItems}
            />
            <Subsection
              title={t('privacy.sections.informationWeCollect.device.title')}
              intro={t('privacy.sections.informationWeCollect.device.intro')}
              items={deviceItems}
            />
            <Subsection
              title={t('privacy.sections.informationWeCollect.payment.title')}
              intro={t('privacy.sections.informationWeCollect.payment.intro')}
              items={paymentItems}
              note={t('privacy.sections.informationWeCollect.payment.note')}
            />
            <Subsection
              title={t('privacy.sections.informationWeCollect.communications.title')}
              intro={t('privacy.sections.informationWeCollect.communications.intro')}
              items={communicationsItems}
            />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t('privacy.sections.googleWorkspace.title')}
            </h2>
            <Paragraphs items={googleIntro} />
            <Subsection
              title={t('privacy.sections.googleWorkspace.drive.title')}
              paragraphs={driveParagraphs}
              items={driveItems}
              closing={driveClosing}
            />
            <Subsection
              title={t('privacy.sections.googleWorkspace.sheets.title')}
              paragraphs={sheetsParagraphs}
              numbered={sheetsNumbered}
              closing={sheetsClosing}
            />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t('privacy.sections.googleProcessing.title')}
            </h2>
            <Paragraphs items={googleProcessing} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t('privacy.sections.googleLimitedUse.title')}
            </h2>
            <Paragraphs items={googleLimitedUse} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.howWeUse.title')}</h2>
            <p className="mb-2">{t('privacy.sections.howWeUse.intro')}</p>
            <BulletList items={howWeUseItems} />
            <p className="mt-3">{t('privacy.sections.howWeUse.note')}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.aiMl.title')}</h2>
            <Paragraphs items={aiMl} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.sharing.title')}</h2>
            <p className="mb-3">{t('privacy.sections.sharing.intro')}</p>
            <Subsection
              title={t('privacy.sections.sharing.serviceProviders.title')}
              paragraphs={serviceProviders}
            />
            <Subsection
              title={t('privacy.sections.sharing.legal.title')}
              intro={t('privacy.sections.sharing.legal.intro')}
              items={legalItems}
            />
            <Subsection title={t('privacy.sections.sharing.business.title')} paragraphs={business} />
            <Subsection
              title={t('privacy.sections.sharing.authorization.title')}
              paragraphs={authorization}
            />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.googleAuth.title')}</h2>
            <Paragraphs items={googleAuth} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.retention.title')}</h2>
            <p className="mb-2">{t('privacy.sections.retention.intro')}</p>
            <BulletList items={retentionItems} />
            <div className="mt-3">
              <Paragraphs items={retentionParagraphs} />
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.security.title')}</h2>
            <Paragraphs items={securityParagraphs} />
            <BulletList items={securityItems} />
            <p className="mt-3">{t('privacy.sections.security.note')}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.rights.title')}</h2>
            <p className="mb-2">{t('privacy.sections.rights.intro')}</p>
            <BulletList items={rightsItems} />
            <p className="mt-3">{t('privacy.sections.rights.contactIntro')}</p>
            <p className="mt-2 font-medium text-gray-900">
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1769ff] hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-3">{t('privacy.sections.rights.verify')}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.cookies.title')}</h2>
            <p className="mb-2">{t('privacy.sections.cookies.intro')}</p>
            <BulletList items={cookiesItems} />
            <p className="mt-3">{t('privacy.sections.cookies.note')}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t('privacy.sections.international.title')}
            </h2>
            <Paragraphs items={international} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.children.title')}</h2>
            <Paragraphs items={children} />
            <p className="mt-2 font-medium text-gray-900">
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1769ff] hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-3">{t('privacy.sections.children.closing')}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.thirdParty.title')}</h2>
            <Paragraphs items={thirdParty} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.changes.title')}</h2>
            <Paragraphs items={changes} />
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{t('privacy.sections.contact.title')}</h2>
            <p className="mb-3">{t('privacy.sections.contact.intro')}</p>
            <p className="font-semibold text-gray-900">{t('privacy.sections.contact.company')}</p>
            <p className="mt-2">
              <strong>{t('privacy.sections.contact.emailLabel')}</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1769ff] hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1">
              <strong>{t('privacy.sections.contact.websiteLabel')}</strong>{' '}
              <a
                href={t('privacy.sections.contact.website')}
                className="text-[#1769ff] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('privacy.sections.contact.website')}
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link to="/" className="text-[#1769ff] font-medium hover:underline">
            {t('privacy.backToHome')}
          </Link>
        </div>
      </article>
    </div>
  );
}
